import { useEffect, useState } from 'react';
import { ProxiedSignInPage, UserIdentity } from '@backstage/core-components';
import {
  BackstageIdentityResponse,
  configApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import {
  SignInPageBlueprint,
  SignInPageProps,
} from '@backstage/plugin-app-react';
import logo from '../nav/LogoFull.svg';
import symbol from '../nav/LogoIcon.svg';
import './signIn.css';

// ponytail: a guest stays a guest for the rest of the browser tab, sign-out included;
// open a new tab to get the sign-in page back. Local development only.
const GUEST_KEY = 'quasar-signin-guest';

const MicrosoftLogo = () => (
  <svg width="21" height="21" viewBox="0 0 21 21" aria-hidden="true">
    <rect width="10" height="10" fill="#f25022" />
    <rect x="11" width="10" height="10" fill="#7fba00" />
    <rect y="11" width="10" height="10" fill="#00a4ef" />
    <rect x="11" y="11" width="10" height="10" fill="#ffb900" />
  </svg>
);

const QuasarSignInPage = (props: SignInPageProps) => {
  const { onSignInSuccess } = props;
  const authApi = useApi(microsoftAuthApiRef);
  const isDevelopment =
    useApi(configApiRef).getOptionalString('auth.environment') ===
    'development';
  const [state, setState] = useState<'checking' | 'idle' | 'busy' | 'guest'>(
    isDevelopment && sessionStorage.getItem(GUEST_KEY) ? 'guest' : 'checking',
  );
  const [error, setError] = useState<string>();

  const complete = async (response: BackstageIdentityResponse) => {
    const profile = await authApi.getProfile();
    onSignInSuccess(
      UserIdentity.create({ identity: response.identity, authApi, profile }),
    );
  };

  // Restores an existing session, so a page reload does not ask for another click.
  useEffect(() => {
    if (state === 'guest') {
      return;
    }
    authApi
      .getBackstageIdentity({ optional: true })
      .then(response => (response ? complete(response) : setState('idle')))
      .catch(() => setState('idle'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = async () => {
    setError(undefined);
    setState('busy');
    try {
      const response = await authApi.getBackstageIdentity({
        instantPopup: true,
      });
      if (!response) {
        throw new Error('Microsoft sign-in is not configured');
      }
      await complete(response);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setState('idle');
    }
  };

  // Guest sign-in is offered in local development only; the backend refuses it elsewhere too.
  if (state === 'guest') {
    return <ProxiedSignInPage {...props} provider="guest" />;
  }

  return (
    <main className="quasar-signin">
      <img className="quasar-signin-backdrop" src={symbol} alt="" />
      <div className="quasar-signin-panel">
        <h1>
          <img src={logo} alt="Quasar" height={56} />
        </h1>
        <p className="quasar-signin-tagline">Dotify developer portal</p>
        <div
          className="quasar-signin-actions"
          hidden={state === 'checking'}
          aria-busy={state === 'busy'}
        >
          <button
            type="button"
            className="quasar-signin-microsoft"
            onClick={signIn}
            disabled={state === 'busy'}
          >
            <MicrosoftLogo />
            Sign in with Microsoft
          </button>
          <p className="quasar-signin-hint">Use your Dotify account.</p>
          {error && (
            <p className="quasar-signin-error" role="alert">
              {error}
            </p>
          )}
          {isDevelopment && (
            <button
              type="button"
              className="quasar-signin-guest"
              onClick={() => {
                sessionStorage.setItem(GUEST_KEY, '1');
                setState('guest');
              }}
            >
              Continue as guest (local development)
            </button>
          )}
        </div>
      </div>
    </main>
  );
};

export const signInModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    SignInPageBlueprint.make({
      params: { loader: async () => QuasarSignInPage },
    }),
  ],
});
